import { apiClient } from "@/lib/api-client";
import type { User } from "@/types/auth";

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export type SocialProvider = "google_auth" | "facebook_auth";

export interface DeviceInfo {
  deviceId?: string;
  deviceName?: string;
  deviceType?: string;
  platform?: string;
  osVersion?: string;
  appVersion?: string;
  pushToken?: string;
}

// ---------------------------------------------------------------------------
// Registration (two-step)
// ---------------------------------------------------------------------------

export interface RegistrationInitiateRequest {
  authMethod: string;
  fields: Record<string, string>;
}

export interface RegistrationInitiateResponse {
  sessionId: string;
  requiresVerification: boolean;
  credentialType: string;
  credentialValue: string;
  expiresAt: string;
  message: string;
}

export interface RegistrationCompleteRequest {
  sessionId: string;
  verificationCode?: string;
  deviceInfo?: DeviceInfo;
  anonymousId?: string;
}

export interface RegistrationCompleteResponse {
  success: boolean;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  user: {
    id: string;
    username?: string;
    email?: string;
    phone?: string;
    createdAt?: string;
    updatedAt?: string;
  };
  migration?: {
    success: boolean;
    itemsMigrated: number;
    errors: string[];
  };
}

export interface AuthFieldDefinition {
  key: string;
  label: string;
  type: string;
  required: boolean;
}

export interface LinkedCredential {
  type: string;
  value: string;
  isVerified: boolean;
}

export interface LinkableAuthMethod {
  authMethod: string;
  name: string;
  fields: AuthFieldDefinition[];
}

export interface LinkableMethodsResponse {
  currentCredentials: LinkedCredential[];
  availableMethods: LinkableAuthMethod[];
  hasPassword: boolean;
}

export type CredentialLinkAction = "link" | "update";

export interface CredentialLinkInitiateRequest {
  authMethod: string;
  fields: Record<string, string>;
  action?: CredentialLinkAction;
}

export interface CredentialLinkInitiateResponse {
  sessionId: string | null;
  requiresVerification: boolean;
  credentialType: string;
  message: string;
  expiresAt: string | null;
}

export interface CredentialLinkCompleteRequest {
  sessionId: string;
  verificationCode?: string;
}

export interface CredentialLinkCompleteResponse {
  success: boolean;
  credentialType: string;
  message: string;
}

export interface UpdatePasswordRequest {
  currentPassword?: string;
  newPassword: string;
}

export interface UpdatePasswordResponse {
  success: boolean;
  message: string;
  hasPassword: boolean;
}

// ---------------------------------------------------------------------------
// Login
// ---------------------------------------------------------------------------

export interface LoginRequest {
  authMethod: string;
  credential: string;
  credentialType?: string;
  password?: string;
  verificationCode?: string;
  deviceInfo?: DeviceInfo;
  anonymousId?: string;
}

export interface LoginResponse {
  success: boolean;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  userId: string;
  message?: string;
  migration?: {
    success: boolean;
    itemsMigrated: number;
    errors: string[];
  };
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

export function registerInitiate(data: RegistrationInitiateRequest) {
  return apiClient.post<RegistrationInitiateResponse>(
    "/api/auth/register/initiate",
    data,
  );
}

export function registerComplete(data: RegistrationCompleteRequest) {
  return apiClient.post<RegistrationCompleteResponse>(
    "/api/auth/register/complete",
    data,
  );
}

export function loginUser(data: LoginRequest) {
  return apiClient.post<LoginResponse>("/api/auth/login", data);
}

export function socialLogin(provider: SocialProvider, token: string) {
  return loginUser({
    authMethod: provider,
    credential: token,
    credentialType: "social_id",
  });
}

export function getCurrentUser() {
  return apiClient.get<User>("/api/customer/profile");
}

export function getLinkableMethods() {
  return apiClient.get<LinkableMethodsResponse>("/api/auth/link/methods");
}

export function initiateCredentialLink(data: CredentialLinkInitiateRequest) {
  return apiClient.post<CredentialLinkInitiateResponse>(
    "/api/auth/link/initiate",
    data,
  );
}

export function completeCredentialLink(data: CredentialLinkCompleteRequest) {
  return apiClient.post<CredentialLinkCompleteResponse>(
    "/api/auth/link/complete",
    data,
  );
}

export function updatePassword(data: UpdatePasswordRequest) {
  return apiClient.put<UpdatePasswordResponse>("/api/auth/password", data);
}

// ---------------------------------------------------------------------------
// Password reset
// ---------------------------------------------------------------------------

export interface ResetPasswordRequest {
  credentialType: string;
  credential: string;
  authMethod: string;
}

export interface ResetPasswordConfirmRequest {
  credentialType: string;
  credential: string;
  resetToken: string;
  newPassword: string;
  authMethod: string;
}

export function resetPassword(data: ResetPasswordRequest) {
  return apiClient.post<{ message: string }>("/api/auth/reset-password", data);
}

export function resetPasswordConfirm(data: ResetPasswordConfirmRequest) {
  return apiClient.post<{ message: string }>(
    "/api/auth/reset-password/confirm",
    data,
  );
}

// ---------------------------------------------------------------------------
// Account recovery (backup codes)
// ---------------------------------------------------------------------------

export interface RecoverAccountRequest {
  credential: string;
  credentialType?: string;
  recoveryCode: string;
}

export interface RecoverAccountResponse {
  success: boolean;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  remainingRecoveryCodes: number;
  message: string;
}

export function recoverAccount(data: RecoverAccountRequest) {
  return apiClient.post<RecoverAccountResponse>("/api/auth/recover", data);
}
